import React, { useState } from "react";
import api from "@/lib/api";
import { downloadCsv } from "@/lib/csv";
import { Terminal, Play, Download } from "lucide-react";

const EXAMPLES = [
  { label: "About", expr: "readAll(about)" },
  { label: "Sites", expr: "readAll(site)" },
  { label: "Points w/ curVal", expr: "readAll(point and cur)" },
  { label: "Energy sensors", expr: "readAll(point and elec and energy and sensor)" },
];

export default function AxonPage() {
  const [expr, setExpr] = useState("readAll(site)");
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    setErr(null);
    setResult(null);
    try {
      const { data } = await api.post("/axon/eval", { expr });
      setResult(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-6xl fade-up">
      <div className="k-label flex items-center gap-2">
        <Terminal className="h-3.5 w-3.5" /> Advanced
      </div>
      <h1 className="mt-2 text-4xl sm:text-5xl font-bold font-display text-[#212121]">Query Console</h1>
      <p className="mt-3 text-[#4D4D4D] text-sm max-w-2xl">
        Run advanced Axon queries directly against the live data engine. Admin only.
      </p>

      <div className="mt-8 k-card p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="k-sublabel">Expression</div>
          <div className="flex flex-wrap gap-2">
            {EXAMPLES.map((ex, i) => (
              <button
                key={ex.label}
                onClick={() => setExpr(ex.expr)}
                data-testid={`axon-example-${i}`}
                className="text-[11px] font-heading font-semibold px-2.5 py-1 rounded-md border border-[#E5E7EB] text-[#6B7280] hover:border-[#156F5B] hover:text-[#156F5B]"
                style={{ transition: "color .2s ease, border-color .2s ease" }}
              >
                {ex.label}
              </button>
            ))}
          </div>
        </div>
        <textarea
          value={expr}
          onChange={(e) => setExpr(e.target.value)}
          rows={4}
          data-testid="axon-expr-input"
          className="w-full bg-[#F9FAFB] text-[#156F5B] font-mono text-sm p-4 rounded-md border border-[#E5E7EB] outline-none focus:border-[#156F5B]"
          style={{ transition: "border-color .2s ease" }}
        />
        <div className="mt-3 flex justify-end">
          <button onClick={run} disabled={busy || !expr.trim()} data-testid="axon-run-btn" className="k-btn !py-2">
            <Play className="h-3 w-3" /> {busy ? "Running…" : "Run"}
          </button>
        </div>
      </div>

      {err && (
        <div className="mt-4 rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-4 py-3" data-testid="axon-error">
          {err}
        </div>
      )}

      {result && (
        <div className="mt-6 k-card overflow-hidden" data-testid="axon-result">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#E5E7EB] bg-[#F9FAFB]">
            <div className="k-sublabel">
              Result • {result.rows?.length ?? 0} rows • {result.cols?.length ?? 0} cols
            </div>
            <button
              onClick={() => {
                const cols = (result.cols || []).map((c) => c.name);
                downloadCsv(
                  "axon_result.csv",
                  cols,
                  (result.rows || []).map((row) => cols.map((c) => row[c]))
                );
              }}
              data-testid="axon-export-csv-btn"
              className="k-btn-outline !py-1 !px-3 !text-[11px]"
            >
              <Download className="h-3 w-3" /> Export CSV
            </button>
          </div>
          <div className="overflow-auto max-h-[560px]">
            <table className="w-full text-xs">
              <thead className="bg-[#F9FAFB] sticky top-0">
                <tr>
                  {(result.cols || []).map((c) => (
                    <th key={c.name} className="text-left px-3 py-2 border-b border-[#E5E7EB] text-[#6B7280] font-heading font-semibold uppercase tracking-wider text-[10px] whitespace-nowrap">{c.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(result.rows || []).map((row, i) => (
                  <tr key={`row-${i}-${JSON.stringify(row[(result.cols || [])[0]?.name] ?? "")}`} className="border-b border-[#F0F2F1] hover:bg-[#F9FAFB]">
                    {(result.cols || []).map((c) => {
                      const v = row[c.name];
                      const rendered = v == null ? "—" : typeof v === "object" ? JSON.stringify(v) : String(v);
                      return (
                        <td key={c.name} className="px-3 py-2 text-[#4D4D4D] whitespace-nowrap max-w-xs truncate" title={rendered}>
                          {rendered}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
